/**
 * OAuthButton Component
 * OAuth-style button with icon support (like Google Sign-In)
 * Uses the same gray fill styling as SecondaryButton for visual consistency
 */

import { designTokens } from '../../constants/designTokens';

interface OAuthButtonProps {
  label: string;
  icon: string;
  darkIcon?: string;
  onClick: () => void;
  fullWidth?: boolean;
  className?: string;
}

export function OAuthButton({
  label,
  icon,
  darkIcon,
  onClick,
  fullWidth = false,
  className = '',
}: OAuthButtonProps) {
  const resolvedIcon = darkIcon
    ? document.documentElement.getAttribute('data-theme') === 'dark' ? darkIcon : icon
    : icon;

  return (
    <>
      <button
        type="button"
        className={`oauth-button ${fullWidth ? 'oauth-button-full' : ''} ${className}`}
        onClick={onClick}
      >
        <img src={resolvedIcon} alt="" className="oauth-button-icon" />
        <span className="oauth-button-label">{label}</span>
      </button>

      <style>{`
        .oauth-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: ${designTokens.spacing.md};
          padding: ${designTokens.spacing.md} ${designTokens.spacing.lg};
          background-color: ${designTokens.colors.background.secondary};
          color: ${designTokens.colors.text.primary};
          border: 1px solid ${designTokens.colors.background.tertiary};
          border-radius: ${designTokens.components.button.borderRadius};
          font-family: ${designTokens.typography.fontFamily.primary};
          font-size: ${designTokens.typography.fontSize.base};
          font-weight: ${designTokens.typography.fontWeight.semibold};
          cursor: pointer;
          transition: ${designTokens.components.button.transition};
          min-height: ${designTokens.components.button.height};
          outline: none;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }

        .oauth-button:hover {
          background-color: ${designTokens.colors.background.tertiary};
        }

        .oauth-button:active {
          transform: scale(0.98);
        }

        .oauth-button:focus-visible {
          box-shadow: 0 0 0 3px ${designTokens.colors.withOpacity.brandFocus};
        }

        .oauth-button-icon {
          width: 24px;
          height: 24px;
          object-fit: contain;
          flex-shrink: 0;
        }

        .oauth-button-label {
          text-align: center;
          white-space: nowrap;
        }

        /* Full width variant */
        .oauth-button-full {
          width: 100%;
        }

        /* Mobile (up to 767px) */
        ${designTokens.media.mobile} {
          .oauth-button:not(.oauth-button-full) {
            width: ${designTokens.components.button.width.mobile};
            margin: 0 auto;
          }

          .oauth-button {
            min-height: ${designTokens.components.button.height};
          }
        }

        /* Tablet (768px-1023px) */
        ${designTokens.media.tablet} {
          .oauth-button:not(.oauth-button-full) {
            width: ${designTokens.components.button.width.tablet};
            margin: 0 auto;
          }

          .oauth-button {
            min-height: ${designTokens.components.button.height};
          }
        }

        /* Desktop (1024px+) */
        ${designTokens.media.desktop} {
          .oauth-button:not(.oauth-button-full) {
            width: ${designTokens.components.button.width.desktop};
            margin: 0 auto;
          }

          .oauth-button {
            min-height: ${designTokens.components.button.height};
          }
        }
      `}</style>
    </>
  );
}
